<template>
  <div class="file-upload-widget">
    <input
      ref="fileInput"
      type="file"
      @change="handleFile"
      style="display: none"
      accept=".png,.jpg,.jpeg,.pdf,.xls,.xlsx,.xlsm,.csv,image/png,image/jpeg,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,text/csv"
    />

    <!-- Upload Area -->
    <div
      v-if="!hasFile"
      class="upload-area"
      :class="{ 'drag-over': dragOver, 'uploading': uploading }"
      @click="triggerFileSelect"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop="handleDrop"
    >
      <div class="upload-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
        </svg>
      </div>
      <div class="upload-text">
        <div class="primary-text">{{ uploading ? 'Processing...' : 'Click to upload or drag and drop' }}</div>
        <div class="secondary-text">{{ uploading ? 'Please wait' : 'PNG, JPG, PDF, Excel, CSV files only - Max 5MB' }}</div>
      </div>
    </div>

    <!-- File Preview -->
    <div v-else class="file-preview">
      <div class="file-info">
        <div class="file-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
          </svg>
        </div>
        <div class="file-details">
          <div class="file-name">{{ displayFileName }}</div>
          <div class="file-size">{{ displayFileSize }}</div>
        </div>
      </div>
      <button type="button" class="remove-button" @click="removeFile">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileWidget',
  props: {
    value: {
      type: [String, File, Object],
      default: ''
    }
  },

  data() {
    return {
      dragOver: false,
      fileName: '',
      fileSize: '',
      uploading: false,
      errorMessage: ''
    };
  },

  computed: {
    hasFile() {
      return this.value && (
        (typeof this.value === 'string' && this.value.length > 0) ||
        this.value instanceof File ||
        this.fileName
      );
    },

    isUrl() {
      return typeof this.value === 'string' && (
        this.value.startsWith('http') ||
        this.value.startsWith('//') ||
        this.value.startsWith('/') ||
        this.value.includes('.')
      );
    },

    displayFileName() {
      if (this.isUrl) {
        // Extract filename from URL - handle encoded characters
        const urlParts = this.value.split('/');
        let filename = urlParts[urlParts.length - 1].split('?')[0];

        // Decode URL encoded characters (like %20 for spaces)
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {
          // If decoding fails, use original filename
        }

        return filename || 'External file';
      }
      return this.fileName || 'Selected file';
    },

    displayFileSize() {
      if (this.isUrl) {
        return 'External file';
      }
      return this.fileSize || '';
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.fileName = '';
          this.fileSize = '';
        } else if (typeof newVal === 'string' && (
          newVal.startsWith('http') ||
          newVal.startsWith('//') ||
          newVal.startsWith('/') ||
          newVal.includes('.')
        )) {
          // Handle URL from backend
          const urlParts = newVal.split('/');
          let filename = urlParts[urlParts.length - 1].split('?')[0];

          // Decode URL encoded characters
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // Keep original if decode fails
          }

          this.fileName = filename || 'External file';
          this.fileSize = 'External file';
        } else if (newVal instanceof File) {
          // Handle File object
          this.fileName = newVal.name;
          this.fileSize = this.formatFileSize(newVal.size);
        }
      }
    }
  },

  methods: {
    handleFile(event) {
      const file = event.target.files[0];
      if (file) {
        if (this.validateFile(file)) {
          this.processFile(file);
        } else {
          // Clear the input
          event.target.value = '';
        }
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        if (this.validateFile(file)) {
          this.processFile(file);
        }
      }
    },

    validateFile(file) {
      this.errorMessage = '';

      // Check file type
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'text/csv'
      ];
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.pdf', '.xls', '.xlsx', '.xlsm', '.csv'];

      const isValidType = allowedTypes.includes(file.type) ||
                         allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isValidType) {
        this.errorMessage = 'Only PNG, JPG, PDF, Excel, and CSV files are allowed';
        this.showError();
        return false;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than 5MB';
        this.showError();
        return false;
      }

      return true;
    },

    showError() {
      // Show error for 4 seconds
      setTimeout(() => {
        this.errorMessage = '';
      }, 4000);
    },

    processFile(file) {
      this.errorMessage = '';
      this.uploading = true;

      this.fileName = file.name;
      this.fileSize = this.formatFileSize(file.size);
      this.originalFile = file; // keep File for submit

      // Convert to base64 string
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploading = false;
        const dataUrl = e.target.result; // base64 string
        this.$emit('input', dataUrl);    // send string to form
      };
      reader.readAsDataURL(file);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    removeFile() {
      this.fileName = '';
      this.fileSize = '';
      this.errorMessage = '';
      this.$emit('input', '');
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    triggerFileSelect() {
      this.$refs.fileInput.click();
    }
  }
};
</script>

<style scoped>
.file-upload-widget {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #f9fafb;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-area.drag-over {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: scale(1.02);
}

.upload-area.uploading {
  cursor: not-allowed;
  opacity: 0.7;
}

.upload-icon {
  color: #6b7280;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.upload-area:hover .upload-icon {
  color: #3b82f6;
}

.upload-text {
  color: #374151;
}

.primary-text {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.secondary-text {
  font-size: 14px;
  color: #6b7280;
}

.file-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.file-preview:hover {
  background: #f1f5f9;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-icon {
  color: #3b82f6;
  margin-right: 12px;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  color: #1f2937;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  color: #6b7280;
  font-size: 12px;
  margin-top: 2px;
}

.remove-button {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-button:hover {
  background: #fef2f2;
  color: #dc2626;
}

/* Loading animation */
.uploading .upload-icon svg {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-message {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  animation: slideIn 0.3s ease;
}

.error-message svg {
  margin-right: 8px;
  flex-shrink: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
